import { Level } from "@cryptors/api-client";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { IResourceComponentsProps } from "@refinedev/core";
import { List, EditButton, ShowButton, useDataGrid } from "@refinedev/mui";
import React, { useMemo } from "react";

import { DeleteIconButton } from "@/components/forms/DeleteIconButton";
import { getTranslation } from "@/utils/translation";

type LevelRow = Level & { name?: string };

export const LevelList: React.FC<IResourceComponentsProps> = () => {
  const { dataGridProps } = useDataGrid<Level>({
    resource: "levels",
    pagination: { pageSize: 25 },
    sorters: { initial: [{ field: "level", order: "asc" }] },
  });

  const rows = useMemo(
    () =>
      (dataGridProps.rows as Level[]).map((level) => ({
        ...level,
        name: getTranslation(level.nameTranslation)?.value,
      })),
    [dataGridProps.rows],
  );

  const columns = useMemo<GridColDef<LevelRow>[]>(
    () => [
      {
        field: "level",
        headerName: "Niveau",
        type: "number",
        minWidth: 50,
      },
      {
        field: "name",
        flex: 1,
        headerName: "Nom",
        sortable: false,
        minWidth: 200,
      },
      {
        field: "pointsRequired",
        flex: 1,
        headerName: "Points requis",
        minWidth: 200,
      },
      {
        field: "image.url",
        headerName: "Image",
        sortable: false,
        minWidth: 50,
        valueGetter: (_value, row) => row.image?.url,
        renderCell: function render({ value }) {
          return <img src={value} alt="Badge" width="50" />;
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        sortable: false,
        renderCell: function render({ row }) {
          return (
            <>
              <EditButton hideText recordItemId={row.level} />
              <ShowButton hideText recordItemId={row.level} />
              <DeleteIconButton recordItemId={row.level} />
            </>
          );
        },
        align: "center",
        headerAlign: "center",
        minWidth: 160,
      },
    ],
    [],
  );

  return (
    <List>
      <DataGrid
        {...dataGridProps}
        rows={rows}
        columns={columns}
        autoHeight
        getRowId={(row) => row.level}
        disableColumnFilter
        disableColumnSelector
        disableDensitySelector
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </List>
  );
};
